import type { Task } from '@/models/Task'

/**
 * MYAC-719 — once a task is accepted, its authoritative copy is the kanban
 * `ProjectTask` (`projects/{pid}/tasks/{tid}`), not the assessment/td_latest
 * source copy. A task qualifies when it is accepted AND has been mirrored to a
 * kanban doc (`project_task_ref`, set at accept). It deliberately does NOT
 * depend on `source_assessment_id` — that field only governs the server-side
 * rules freeze (which tasks are protected); the read/write routing should
 * follow the kanban copy whenever one exists. For these tasks the view shows —
 * and all edits write to — the kanban copy.
 */
export function isAcceptedKanbanTask(task: Task): boolean {
  return task.effectiveDecision === 'accepted' && !!task.project_task_ref
}

const ASSESSMENT_ID_RE = /\/assessments\/([^/]+)/

/** Extract the assessment id from a Firestore task path, or null. */
export function assessmentIdFromPath(path: string | null | undefined): string | null {
  if (!path) return null
  const m = path.match(ASSESSMENT_ID_RE)
  return m ? m[1] : null
}

/**
 * Assessment id a task belongs to. Kanban tasks now carry `path` pointing at
 * the kanban doc (no assessment segment), so prefer the stamped
 * `source_assessment_id` and fall back to parsing the path for source tasks.
 */
export function taskAssessmentId(task: Task): string | null {
  return task.source_assessment_id ?? assessmentIdFromPath(task.path)
}

/**
 * On un-accept, the source copy to flip back to pending/rejected:
 *  - QMS lives in a result subcollection (`source_assessment_ref` contains
 *    `/results/`) — flip that exact doc.
 *  - TD lives under the ephemeral `td_latest` assessment (the historical
 *    `source_assessment_ref` is frozen/irrelevant) — flip `td_latest`.
 */
export function resolveUnacceptSourcePath(
  sourceAssessmentRef: string | null | undefined,
  pid: string,
  taskId: string,
): string {
  if (sourceAssessmentRef && sourceAssessmentRef.includes('/results/')) {
    return sourceAssessmentRef
  }
  return `projects/${pid}/assessments/td_latest/tasks/${taskId}`
}

/**
 * MYAC-719 × MYAC-708 — a grouping subject stores its tasks as relative refs
 * `results/{rid}/tasks/{tid}` (the source-task namespace). Once a task is
 * accepted it is swapped for its kanban copy whose path is
 * `projects/{pid}/tasks/{tid}` — the `results/...` prefix is gone, so matching a
 * ref against a task's *path* (suffix/prefix) silently drops accepted tasks
 * from their subject. The trailing segment — the task id — is identical on the
 * source doc and the kanban copy, so always join on the id.
 */
export function taskIdFromRef(ref: string): string {
  return ref.split('/').pop() ?? ref
}

/**
 * Resolve which of `tasks` belong to a subject, joining the subject's
 * `taskRefs` to tasks by task **id** (see {@link taskIdFromRef}). Order follows
 * `taskRefs`; refs with no loaded task are skipped. Id-based so accepted tasks
 * (now backed by their kanban copy) still resolve.
 */
export function tasksForRefs<T extends { id: string }>(
  taskRefs: string[] | null | undefined,
  tasksById: Map<string, T>,
): T[] {
  const out: T[] = []
  for (const ref of taskRefs ?? []) {
    const task = tasksById.get(taskIdFromRef(ref))
    if (task) out.push(task)
  }
  return out
}
